import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { nameof } from 'ts-simple-nameof';
import { PagedSearchDto } from '../../../../../../libs/utils/ts-shared/src/lib/api/searching/dtos/PagedSearchDto';
import { GridMenuItem } from '../../../../../../libs/utils/ng-shared-components/src/lib/grid/grid-menu/grid-menu-item';
import { GridComponent, IGridActionButton } from '../../../../../../libs/utils/ng-shared-components/src/lib/grid/grid.component';
import { CellValueType, IGridColumn } from '../../../../../../libs/utils/ng-shared-components/src/lib/grid/interface/grid-column';
import { ModalService } from '../../../../../../libs/utils/ng-shared-components/src/lib/services/modal.service';
import { AccreditationDto } from '../../../api/dtos/AccreditationDto';
import { HttpApiRequestOptions } from '../../../api/endpoints/HttpApiRequestOptions';
import { RunJobParameter } from '../../../api/dtos/RunJobParameter';
import { AccreditationFiltersDto } from '../../../api/dtos/AccreditationFiltersDto';
import { enumName_mi_pspc } from '../../../api/enums/_enumName.mi_pspc';
import { ApiAdminAccreditation } from '../../../api/endpoints/ApiAdminAccreditation';
import { FilterType } from '../../../../../../libs/utils/ng-shared-components/src/lib/grid/interface/filter-type';
import { AccreditationDetailsComponent } from './accreditation-details.component';
import { Icons } from '../../../../../../libs/utils/ng-shared-components/src/lib/icon/icons';
import { AccreditationStatus } from '../../../api/enums/AccreditationStatus';
import { AccreditationUndoComponent } from './accreditation-undo.component';
import { NotificationService } from '../../../../../../libs/utils/ng-shared-components/src';
import { AccreditationsByAccountPopupComponent } from './accreditations-by-account/accreditations-by-account-popup.component';
import { TransferPopupComponent } from '../payment-methods/transfer/transfer-popup.component';
import { EnumTranslateProviderService } from '../../../../../../libs/utils/ng-shared-components/src/lib/services/enum-translate-provider.service';

@Component({
  standalone: false,
  selector: 'mf-accreditation-grid',
  templateUrl: './accreditation-grid.component.html',
  styleUrls: ['./accreditation-grid.component.scss'],
})
export class AccreditationGridComponent implements OnInit {
  constructor(
    private _apiAdminAccreditation: ApiAdminAccreditation,
    private _modalService: ModalService,
    private _route: ActivatedRoute,
    private _notificationService: NotificationService,
    private _enumTranslateProviderService: EnumTranslateProviderService
  ) {}

  @ViewChild('grid')
  grid: GridComponent;

  @Input() showCreateButton = false;
  @Input() disableCreateButton = true;

  filters = new AccreditationFiltersDto();
  jobBody: RunJobParameter;

  ngOnInit(): void {
    this._route.queryParams.subscribe((params) => {
      const accreditationId = params['openFileUpload'];

      if (accreditationId) {
        setTimeout(() => {
          this.openFileUploadModal(accreditationId);
        }, 500);
      }
    });
  }

  openFileUploadModal(accreditationId: string): void {
    this._apiAdminAccreditation
      .getById(accreditationId)
      .then((accreditation) => {
        if (!accreditation) {
          this._notificationService.showError('No se encontró la acreditación.');
          return;
        }

        if (accreditation.status !== AccreditationStatus.WaitingFilesUploadConfirmation) {
          this._notificationService.showError(
            'La acreditación no está en el estado: ' +
              this._enumTranslateProviderService.translateEnum('AccreditationStatus', AccreditationStatus.WaitingFilesUploadConfirmation)
          );
          return;
        }

        this._modalService.openRightModal(AccreditationsByAccountPopupComponent, {
          openData: accreditation.id,
          onCloseSuccess: () => {
            this.grid?.reloadPage();
          },
          onCloseCancel: () => {
            this.grid?.reloadPage();
          },
        });
      })
      .catch(() => {
        this._notificationService.showError('Error al obtener la acreditación.');
      });
  }

  columns: IGridColumn<AccreditationDto>[] = [
    {
      header: 'Número',
      field: nameof<AccreditationDto>((e) => e.accreditationNumber),
      filter: {
        propertyName: nameof<AccreditationFiltersDto>((e) => e.accreditationNumber),
        type: FilterType.NumericRange,
      },
      width: 5,
    },
    {
      header: 'Fecha',
      field: nameof<AccreditationDto>((e) => e.date),
      filter: {
        propertyName: nameof<AccreditationFiltersDto>((e) => e.date),
        type: FilterType.Date,
      },
      width: 10,
      cellOptions: { type: CellValueType.Date, dateIncludeTime: true },
    },
    {
      header: 'Tipo',
      field: nameof<AccreditationDto>((e) => e.type),
      filter: {
        propertyName: nameof<AccreditationFiltersDto>((e) => e.type),
        type: FilterType.Enum,
        enumName: enumName_mi_pspc.AccreditationType,
      },
      cellOptions: { enumName: enumName_mi_pspc.AccreditationType, type: CellValueType.Enum },
      width: 10,
    },
    {
      header: 'Estado',
      field: nameof<AccreditationDto>((e) => e.status),
      filter: {
        propertyName: nameof<AccreditationFiltersDto>((e) => e.statusList),
        type: FilterType.EnumList,
        enumName: enumName_mi_pspc.AccreditationStatus,
      },
      width: 10,
      cellOptions: {
        type: CellValueType.Enum,
        enumName: enumName_mi_pspc.AccreditationStatus,
      },
    },
    {
      header: 'Conciliaciones',
      field: nameof<AccreditationDto>((e) => e.conciliatedTransfersCount),
      width: 7,
      align: 'right',
    },
    {
      header: 'Mov. actualizados',
      field: nameof<AccreditationDto>((e) => e.updatedBankMovementsCount),
      width: 7,
      align: 'right',
    },
    {
      header: 'Nro. archivos',
      field: nameof<AccreditationDto>((e) => e.accreditationsByAccounts.length),
      width: 7,
      align: 'right',
    },
    {
      header: 'Transferencias',
      field: nameof<AccreditationDto>((e) => e.generatedTransfersCount),
      width: 7,
      align: 'right',
    },
  ];

  menuItems: GridMenuItem[] = [
    {
      label: 'Ver detalles',
      icon: Icons.details,
      onClick: (item: AccreditationDto) => {
        this._modalService.openRightModal(AccreditationDetailsComponent, { openData: item.id });
      },
    },
    {
      label: 'Carga de archivos',
      icon: Icons.archive,
      onClick: (item: AccreditationDto) => {
        this.openFileUploadModal(item.id);
      },
    },
    {
      label: 'Deshacer',
      icon: Icons.undo,
      onClick: (item: AccreditationDto) => {
        this._modalService.openNormalModal(AccreditationUndoComponent, {
          openData: item.id,
          onCloseSuccess: () => {
            this.grid.reloadPage();
          },
        });
      },
      hide: (item: AccreditationDto) => {
        return item.status >= AccreditationStatus.CompletedOk;
      },
    },
    {
      label: 'Conciliaciones',
      icon: Icons.conciliations,
      onClick: (item: AccreditationDto) => {
        this._modalService.openRightModal(TransferPopupComponent, {
          openData: { accreditationConsolidationNumber: item.accreditationNumber },
          onCloseSuccess: () => {
            this.grid.reloadPage();
          },
        });
      },
    },
    {
      label: 'Transferencias',
      icon: Icons.transfer,
      onClick: (item: AccreditationDto) => {
        this._modalService.openRightModal(TransferPopupComponent, {
          openData: { accreditationOriginNumber: item.accreditationNumber },
          onCloseSuccess: () => {
            this.grid.reloadPage();
          },
        });
      },
    },
  ];

  gridActionButtons: IGridActionButton[] = [
    {
      text: 'Nueva acreditación de pago',
      onClick: () => {
        this.createAccreditationForPayment();
      },
    },
    {
      text: 'Nueva acreditación de compensación',
      isSecondary: true,
      onClick: () => {
        this.createAccreditationForCompensation();
      },
    },
  ];

  apiEndpoint = (search: PagedSearchDto<AccreditationFiltersDto>, httpApiRequestOptions: HttpApiRequestOptions) =>
    this._apiAdminAccreditation.getPaged(search, httpApiRequestOptions);

  createAccreditationForPayment() {
    this._modalService.openYesOrNoModal({
      openData: {
        title: 'Ejecutar proceso de acreditaciones',
        message: 'Un proceso de acreditación, actualiza las conciliaciones y genera los pagos salientes si aplica',
      },
      onCloseSuccess: async () => {
        await this._apiAdminAccreditation.generateForPayment();
        this._notificationService.showSuccess('El proceso de acreditación se inició con éxito');
        this.grid.reloadPage();
      },
    });
  }

  createAccreditationForCompensation() {
    this._modalService.openYesOrNoModal({
      openData: {
        title: 'Ejecutar proceso de compensaciones',
        message: 'Un proceso de compensación, genera transferencias entre las cuentas de la pasarela para compensar los saldos negativos',
      },
      onCloseSuccess: async () => {
        await this._apiAdminAccreditation.generateForCompensation();
        this._notificationService.showSuccess('El proceso de acreditación de compensación se inició con éxito');
        this.grid.reloadPage();
      },
    });
  }
}
